import * as THREE from 'three';
import type { WeaponId } from '../weapons/WeaponDefs';
import { createRocketMesh } from '../weapons/Projectiles';
import type { LoadedViewWeapon } from './ViewmodelAssets';

interface GunMats {
  steel: THREE.MeshStandardMaterial;
  steelDark: THREE.MeshStandardMaterial;
  bruniton: THREE.MeshStandardMaterial;
  alloy: THREE.MeshStandardMaterial;
  polymer: THREE.MeshStandardMaterial;
  polymerSoft: THREE.MeshStandardMaterial;
  wood: THREE.MeshStandardMaterial;
  walnut: THREE.MeshStandardMaterial;
  bakelite: THREE.MeshStandardMaterial;
  brass: THREE.MeshStandardMaterial;
  sight: THREE.MeshStandardMaterial;
  rubber: THREE.MeshStandardMaterial;
}

const texLoader = new THREE.TextureLoader();

function loadGunMap(url: string, rx: number, ry: number, srgb: boolean): THREE.Texture {
  const tex = texLoader.load(url);
  tex.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(rx, ry);
  tex.anisotropy = 8;
  return tex;
}

let woodMapsCache: {
  woodAlbedo: THREE.Texture;
  woodRough: THREE.Texture;
  walnutAlbedo: THREE.Texture;
  walnutRough: THREE.Texture;
} | null = null;

function woodMaps() {
  if (woodMapsCache) return woodMapsCache;
  const woodAlbedo = loadGunMap('/assets/textures/WoodTable001/albedo.jpg', 0.9, 1.6, true);
  const woodRough = loadGunMap('/assets/textures/WoodTable001/roughness.jpg', 0.9, 1.6, false);
  const walnutAlbedo = loadGunMap('/assets/textures/WoodTable001/albedo.jpg', 0.9, 1.6, true);
  walnutAlbedo.offset.set(0.37, 0.22);
  const walnutRough = loadGunMap('/assets/textures/WoodTable001/roughness.jpg', 0.9, 1.6, false);
  walnutRough.offset.set(0.37, 0.22);
  woodMapsCache = { woodAlbedo, woodRough, walnutAlbedo, walnutRough };
  return woodMapsCache;
}

function makeMats(): GunMats {
  const w = woodMaps();

  return {
    steel: new THREE.MeshStandardMaterial({ color: 0x3a424c, metalness: 0.92, roughness: 0.28 }),
    steelDark: new THREE.MeshStandardMaterial({ color: 0x1c2026, metalness: 0.88, roughness: 0.38 }),
    bruniton: new THREE.MeshStandardMaterial({ color: 0x4a5258, metalness: 0.82, roughness: 0.36 }),
    alloy: new THREE.MeshStandardMaterial({ color: 0x6a7278, metalness: 0.55, roughness: 0.42 }),
    polymer: new THREE.MeshStandardMaterial({ color: 0x16181c, metalness: 0.08, roughness: 0.58 }),
    polymerSoft: new THREE.MeshStandardMaterial({ color: 0x2a2e32, metalness: 0.06, roughness: 0.7 }),
    wood: new THREE.MeshStandardMaterial({
      map: w.woodAlbedo,
      roughnessMap: w.woodRough,
      color: 0xd8a878,
      metalness: 0.04,
      roughness: 0.58,
    }),
    walnut: new THREE.MeshStandardMaterial({
      map: w.walnutAlbedo,
      roughnessMap: w.walnutRough,
      color: 0x8a4a28,
      metalness: 0.03,
      roughness: 0.7,
    }),
    bakelite: new THREE.MeshStandardMaterial({ color: 0x6a2e18, metalness: 0.12, roughness: 0.48 }),
    brass: new THREE.MeshStandardMaterial({ color: 0xb08a3a, metalness: 0.85, roughness: 0.32 }),
    sight: new THREE.MeshStandardMaterial({
      color: 0xd8e8c8,
      metalness: 0.15,
      roughness: 0.35,
      emissive: 0x88aa66,
      emissiveIntensity: 0.35,
    }),
    rubber: new THREE.MeshStandardMaterial({ color: 0x121212, metalness: 0.05, roughness: 0.82 }),
  };
}

function markView(root: THREE.Object3D): void {
  root.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.frustumCulled = false;
    mesh.castShadow = false;
    mesh.receiveShadow = false;
  });
}

function add(
  parent: THREE.Object3D,
  geo: THREE.BufferGeometry,
  mat: THREE.Material,
  x: number,
  y: number,
  z: number,
  rx = 0,
  ry = 0,
  rz = 0,
): THREE.Mesh {
  const mesh = new THREE.Mesh(geo, mat);
  mesh.position.set(x, y, z);
  mesh.rotation.set(rx, ry, rz);
  parent.add(mesh);
  return mesh;
}

function barrel(
  parent: THREE.Object3D,
  mat: THREE.Material,
  z0: number,
  z1: number,
  r: number,
  segs = 14,
  r2 = r,
): THREE.Mesh {
  const len = Math.abs(z1 - z0);
  const mesh = new THREE.Mesh(new THREE.CylinderGeometry(r2, r, len, segs), mat);
  mesh.rotation.x = Math.PI / 2;
  mesh.position.set(0, 0, (z0 + z1) * 0.5);
  parent.add(mesh);
  return mesh;
}

function cap(
  parent: THREE.Object3D,
  mat: THREE.Material,
  x: number,
  y: number,
  z: number,
  cylLen: number,
  r: number,
  rx = Math.PI / 2,
  ry = 0,
  rz = 0,
  wide = 1,
  flat = 1,
): THREE.Mesh {
  const mesh = new THREE.Mesh(new THREE.CapsuleGeometry(r, Math.max(0.0008, cylLen), 4, 8), mat);
  mesh.position.set(x, y, z);
  mesh.rotation.set(rx, ry, rz);
  mesh.scale.set(wide, 1, flat);
  parent.add(mesh);
  return mesh;
}

type Vec3 = [number, number, number];

function pushTri(
  pos: number[],
  nrm: number[],
  idx: number[],
  a: Vec3,
  b: Vec3,
  c: Vec3,
  want?: Vec3,
): void {
  let bx = b[0];
  let by = b[1];
  let bz = b[2];
  let cx = c[0];
  let cy = c[1];
  let cz = c[2];
  const abx = bx - a[0];
  const aby = by - a[1];
  const abz = bz - a[2];
  const acx = cx - a[0];
  const acy = cy - a[1];
  const acz = cz - a[2];
  let nx = aby * acz - abz * acy;
  let ny = abz * acx - abx * acz;
  let nz = abx * acy - aby * acx;
  if (want && nx * want[0] + ny * want[1] + nz * want[2] < 0) {
    const tx = bx;
    const ty = by;
    const tz = bz;
    bx = cx;
    by = cy;
    bz = cz;
    cx = tx;
    cy = ty;
    cz = tz;
    nx = -nx;
    ny = -ny;
    nz = -nz;
  }
  const len = Math.hypot(nx, ny, nz) || 1;
  nx /= len;
  ny /= len;
  nz /= len;
  const base = pos.length / 3;
  pos.push(a[0], a[1], a[2], bx, by, bz, cx, cy, cz);
  nrm.push(nx, ny, nz, nx, ny, nz, nx, ny, nz);
  idx.push(base, base + 1, base + 2);
}

function pushQuad(
  pos: number[],
  nrm: number[],
  idx: number[],
  a: Vec3,
  b: Vec3,
  c: Vec3,
  d: Vec3,
  nx: number,
  ny: number,
  nz: number,
): void {
  const abx = b[0] - a[0];
  const aby = b[1] - a[1];
  const abz = b[2] - a[2];
  const acx = c[0] - a[0];
  const acy = c[1] - a[1];
  const acz = c[2] - a[2];
  const gx = aby * acz - abz * acy;
  const gy = abz * acx - abx * acz;
  const gz = abx * acy - aby * acx;
  const verts = gx * nx + gy * ny + gz * nz < 0 ? [a, d, c, b] : [a, b, c, d];
  const base = pos.length / 3;
  for (const p of verts) {
    pos.push(p[0], p[1], p[2]);
    nrm.push(nx, ny, nz);
  }
  idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
}

/** Closed YZ outline extruded along X. High-Y verts inset by `chamfer` for a beveled top. */
function extrudeProfile(pts: Array<[number, number]>, halfW: number, chamfer = 0): THREE.BufferGeometry {
  const n0 = pts.length;
  let minY = Infinity;
  let maxY = -Infinity;
  let area = 0;
  for (let i = 0; i < n0; i++) {
    const y0 = pts[i]![0];
    const z0 = pts[i]![1];
    const y1 = pts[(i + 1) % n0]![0];
    const z1 = pts[(i + 1) % n0]![1];
    minY = Math.min(minY, y0);
    maxY = Math.max(maxY, y0);
    area += z0 * y1 - z1 * y0;
  }
  const ring = area < 0 ? pts.slice().reverse() : pts;
  const n = ring.length;
  const span = Math.max(1e-6, maxY - minY);
  const hwAt = (i: number) => {
    const y = ring[i]![0];
    const t = chamfer > 0 ? (y - minY) / span : 0;
    return Math.max(halfW * 0.22, halfW - chamfer * t);
  };
  const vert = (i: number, sign: number): Vec3 => {
    const hw = hwAt(i);
    return [sign * hw, ring[i]![0], ring[i]![1]];
  };

  const pos: number[] = [];
  const nrm: number[] = [];
  const idx: number[] = [];
  const contour = ring.map(([y, z]) => new THREE.Vector2(z, y));
  let faces = THREE.ShapeUtils.triangulateShape(contour, []);
  if (faces.length === 0 && n >= 3) {
    faces = [];
    for (let i = 1; i < n - 1; i++) faces.push([0, i, i + 1]);
  }
  for (const f of faces) {
    const i0 = f[0]!;
    const i1 = f[1]!;
    const i2 = f[2]!;
    pushTri(pos, nrm, idx, vert(i0, -1), vert(i1, -1), vert(i2, -1), [-1, 0, 0]);
    pushTri(pos, nrm, idx, vert(i0, 1), vert(i1, 1), vert(i2, 1), [1, 0, 0]);
  }
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const a = vert(i, -1);
    const b = vert(j, -1);
    const c = vert(j, 1);
    const d = vert(i, 1);
    const dy = ring[j]![0] - ring[i]![0];
    const dz = ring[j]![1] - ring[i]![1];
    const want: Vec3 = [0, -dz, dy];
    pushTri(pos, nrm, idx, a, b, c, want);
    pushTri(pos, nrm, idx, a, c, d, want);
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute('normal', new THREE.Float32BufferAttribute(nrm, 3));
  geo.setIndex(idx);
  return geo;
}

/** Box with a rectangular U-channel cut into the top face (along Z). */
function topGrooveBox(w: number, h: number, d: number, grooveW: number, grooveH: number): THREE.BufferGeometry {
  const xw = w * 0.5;
  const yw = h * 0.5;
  const zw = d * 0.5;
  const gw = Math.min(grooveW, w - 0.002) * 0.5;
  const gy = yw - Math.min(grooveH, h - 0.004);
  const pos: number[] = [];
  const nrm: number[] = [];
  const idx: number[] = [];
  pushQuad(pos, nrm, idx, [-xw, -yw, -zw], [xw, -yw, -zw], [xw, -yw, zw], [-xw, -yw, zw], 0, -1, 0);
  pushQuad(pos, nrm, idx, [-xw, -yw, zw], [-xw, -yw, -zw], [-xw, yw, -zw], [-xw, yw, zw], -1, 0, 0);
  pushQuad(pos, nrm, idx, [xw, -yw, -zw], [xw, -yw, zw], [xw, yw, zw], [xw, yw, -zw], 1, 0, 0);
  pushQuad(pos, nrm, idx, [-xw, yw, -zw], [-xw, yw, zw], [-gw, yw, zw], [-gw, yw, -zw], 0, 1, 0);
  pushQuad(pos, nrm, idx, [gw, yw, -zw], [gw, yw, zw], [xw, yw, zw], [xw, yw, -zw], 0, 1, 0);
  pushQuad(pos, nrm, idx, [-gw, gy, -zw], [gw, gy, -zw], [gw, gy, zw], [-gw, gy, zw], 0, 1, 0);
  pushQuad(pos, nrm, idx, [-gw, gy, zw], [-gw, gy, -zw], [-gw, yw, -zw], [-gw, yw, zw], 1, 0, 0);
  pushQuad(pos, nrm, idx, [gw, gy, -zw], [gw, gy, zw], [gw, yw, zw], [gw, yw, -zw], -1, 0, 0);
  pushQuad(pos, nrm, idx, [-xw, -yw, -zw], [xw, -yw, -zw], [xw, gy, -zw], [-xw, gy, -zw], 0, 0, -1);
  pushQuad(pos, nrm, idx, [-xw, gy, -zw], [-gw, gy, -zw], [-gw, yw, -zw], [-xw, yw, -zw], 0, 0, -1);
  pushQuad(pos, nrm, idx, [gw, gy, -zw], [xw, gy, -zw], [xw, yw, -zw], [gw, yw, -zw], 0, 0, -1);
  pushQuad(pos, nrm, idx, [xw, -yw, zw], [-xw, -yw, zw], [-xw, gy, zw], [xw, gy, zw], 0, 0, 1);
  pushQuad(pos, nrm, idx, [-gw, gy, zw], [-xw, gy, zw], [-xw, yw, zw], [-gw, yw, zw], 0, 0, 1);
  pushQuad(pos, nrm, idx, [xw, gy, zw], [gw, gy, zw], [gw, yw, zw], [xw, yw, zw], 0, 0, 1);
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute('normal', new THREE.Float32BufferAttribute(nrm, 3));
  geo.setIndex(idx);
  return geo;
}

/** 8-vertex hexahedron, wider at the bottom. Height along Y, depth along Z. */
function taperBox(wTop: number, wBot: number, h: number, d: number): THREE.BufferGeometry {
  const yt = h * 0.5;
  const yb = -h * 0.5;
  const zf = -d * 0.5;
  const zr = d * 0.5;
  const wt = wTop * 0.5;
  const wb = wBot * 0.5;
  const pos: number[] = [];
  const nrm: number[] = [];
  const idx: number[] = [];
  pushQuad(pos, nrm, idx, [-wb, yb, zf], [wb, yb, zf], [wt, yt, zf], [-wt, yt, zf], 0, 0, -1);
  pushQuad(pos, nrm, idx, [wb, yb, zr], [-wb, yb, zr], [-wt, yt, zr], [wt, yt, zr], 0, 0, 1);
  pushQuad(pos, nrm, idx, [-wt, yt, zf], [wt, yt, zf], [wt, yt, zr], [-wt, yt, zr], 0, 1, 0);
  pushQuad(pos, nrm, idx, [-wb, yb, zr], [wb, yb, zr], [wb, yb, zf], [-wb, yb, zf], 0, -1, 0);
  pushQuad(pos, nrm, idx, [-wb, yb, zr], [-wb, yb, zf], [-wt, yt, zf], [-wt, yt, zr], -1, 0, 0);
  pushQuad(pos, nrm, idx, [wb, yb, zf], [wb, yb, zr], [wt, yt, zr], [wt, yt, zf], 1, 0, 0);
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute('normal', new THREE.Float32BufferAttribute(nrm, 3));
  geo.setIndex(idx);
  return geo;
}

function extrudeAlongZ(shape: THREE.Shape, depth: number, curveSegs = 8): THREE.BufferGeometry {
  const geo = new THREE.ExtrudeGeometry(shape, {
    depth,
    bevelEnabled: false,
    steps: 1,
    curveSegments: curveSegs,
  });
  geo.translate(0, 0, -depth * 0.5);
  geo.computeVertexNormals();
  return geo;
}

function hollowTube(rOut: number, rIn: number, length: number, segs = 12): THREE.BufferGeometry {
  const shape = new THREE.Shape();
  shape.absarc(0, 0, rOut, 0, Math.PI * 2, false);
  const hole = new THREE.Path();
  hole.absarc(0, 0, rIn, 0, Math.PI * 2, true);
  shape.holes.push(hole);
  return extrudeAlongZ(shape, length, segs);
}

/** Hollow cone frustum along Y (rotate rx=π/2 for gun −Z). Rear is +Y. */
function taperHollow(
  rOutBack: number,
  rOutFront: number,
  rInBack: number,
  rInFront: number,
  len: number,
  segs = 16,
): THREE.BufferGeometry {
  const hy = len * 0.5;
  const geo = new THREE.LatheGeometry(
    [
      new THREE.Vector2(rOutBack, hy),
      new THREE.Vector2(rOutFront, -hy),
      new THREE.Vector2(rInFront, -hy),
      new THREE.Vector2(rInBack, hy),
    ],
    segs,
  );
  geo.computeVertexNormals();
  return geo;
}

function squareFrame(outerW: number, outerH: number, holeW: number, holeH: number, depth: number): THREE.BufferGeometry {
  const hw = outerW * 0.5;
  const hh = outerH * 0.5;
  const iw = holeW * 0.5;
  const ih = holeH * 0.5;
  const shape = new THREE.Shape();
  shape.moveTo(-hw, -hh);
  shape.lineTo(hw, -hh);
  shape.lineTo(hw, hh);
  shape.lineTo(-hw, hh);
  shape.closePath();
  const hole = new THREE.Path();
  hole.moveTo(-iw, -ih);
  hole.lineTo(iw, -ih);
  hole.lineTo(iw, ih);
  hole.lineTo(-iw, ih);
  hole.closePath();
  shape.holes.push(hole);
  return extrudeAlongZ(shape, depth, 1);
}

function paintOpticGlass(): THREE.CanvasTexture {
  const s = 128;
  const c = document.createElement('canvas');
  c.width = c.height = s;
  const ctx = c.getContext('2d')!;
  ctx.clearRect(0, 0, s, s);
  ctx.fillStyle = 'rgba(190, 230, 80, 0.14)';
  ctx.fillRect(0, 0, s, s);

  const dot = (x: number, y: number, r: number) => {
    const rad = ctx.createRadialGradient(x, y, 0, x, y, r);
    rad.addColorStop(0, 'rgba(255, 255, 170, 0.95)');
    rad.addColorStop(0.35, 'rgba(210, 255, 70, 0.7)');
    rad.addColorStop(1, 'rgba(180, 230, 50, 0)');
    ctx.fillStyle = rad;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  };
  dot(s * 0.5, s * 0.48, 3.2);
  dot(s * 0.3, s * 0.7, 2.2);
  dot(s * 0.7, s * 0.7, 2.2);

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}

function opticGlassMat(): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({
    map: paintOpticGlass(),
    color: 0xffffff,
    transparent: true,
    opacity: 1,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
}

function socket(name: string, x: number, y: number, z: number, parent: THREE.Object3D): THREE.Object3D {
  const n = new THREE.Object3D();
  n.name = name;
  n.position.set(x, y, z);
  parent.add(n);
  return n;
}

const _y = new THREE.Vector3();
const _z = new THREE.Vector3();
const _x = new THREE.Vector3();
const _basis = new THREE.Matrix4();

/** Grip basis: +Y = back of hand, -Z = fingers, +X = thumb. */
function orientGrip(node: THREE.Object3D, backX: number, backY: number, backZ: number, finX: number, finY: number, finZ: number): void {
  _y.set(backX, backY, backZ).normalize();
  _z.set(finX, finY, finZ).normalize().negate();
  _x.crossVectors(_y, _z).normalize();
  _z.crossVectors(_x, _y).normalize();
  _basis.makeBasis(_x, _y, _z);
  node.quaternion.setFromRotationMatrix(_basis);
}

function gripAt(
  name: string,
  parent: THREE.Object3D,
  x: number,
  y: number,
  z: number,
  back: [number, number, number],
  fingers: [number, number, number],
): THREE.Object3D {
  const n = socket(name, x, y, z, parent);
  orientGrip(n, back[0], back[1], back[2], fingers[0], fingers[1], fingers[2]);
  return n;
}

/** Vertical handle: palm on the side, fingers around the frontstrap, wrist toward the shooter. */
/** Right mesh has thumb on −X, so +X is pinky / down the mag. Fingers along the barrel wrap the handle. */
const PISTOL_R_BACK: [number, number, number] = [0.82, 0.22, 0.52];
const PISTOL_R_FINGERS: [number, number, number] = [-0.28, -0.18, -0.94];
/** Left mesh has thumb on +X. Back faces left; fingers wrap the frontstrap. */
const PISTOL_L_BACK: [number, number, number] = [-0.82, 0.28, 0.5];
const PISTOL_L_FINGERS: [number, number, number] = [0.22, -0.12, -0.97];

/** Forend / tube: palm on the barrel, fingers along it. */
const FOREND_BACK: [number, number, number] = [0.06, 0.96, 0.14];
const FOREND_FINGERS: [number, number, number] = [0.08, -0.12, -0.99];

function pistolGripR(parent: THREE.Object3D, x: number, y: number, z: number): THREE.Object3D {
  return gripAt('grip_r', parent, x, y, z, PISTOL_R_BACK, PISTOL_R_FINGERS);
}

function pistolGripL(parent: THREE.Object3D, x: number, y: number, z: number): THREE.Object3D {
  return gripAt('grip_l', parent, x, y, z, PISTOL_L_BACK, PISTOL_L_FINGERS);
}

function forendGripL(parent: THREE.Object3D, x: number, y: number, z: number): THREE.Object3D {
  return gripAt('grip_l', parent, x, y, z, FOREND_BACK, FOREND_FINGERS);
}

export interface M9GripShape {
  pitch: number;
  y: number;
  z: number;
  height: number;
  topW: number;
  botW: number;
  depth: number;
}

export function defaultM9GripShape(): M9GripShape {
  return {
    pitch: 0.11,
    y: -0.005,
    z: 0.04,
    height: 0.059,
    topW: 0.017,
    botW: 0.017,
    depth: 0.032,
  };
}

interface M9GripMats {
  charcoal: THREE.Material;
  polymer: THREE.Material;
  steelDark: THREE.Material;
}

function fillPistolMagMeshes(mag: THREE.Group, mats: M9GripMats, s: M9GripShape): void {
  for (const child of [...mag.children]) {
    mag.remove(child);
    const mesh = child as THREE.Mesh;
    mesh.geometry?.dispose();
  }
  const bodyY = -s.height * 0.52;
  const floorY = bodyY - s.height * 0.52;
  mag.position.set(0, floorY, 0.002);
  mag.rotation.set(0, 0, 0);
  add(mag, taperBox(s.botW * 0.86, s.botW * 0.96, 0.012, s.depth * 1.07), mats.charcoal, 0, 0, 0);
  add(mag, taperBox(s.topW, s.botW, s.height, s.depth), mats.charcoal, 0, bodyY - floorY, 0);
}

function ensurePistolMag(grip: THREE.Group): THREE.Group {
  let mag = grip.getObjectByName('pistol_mag') as THREE.Group | null;
  if (!mag) {
    mag = new THREE.Group();
    mag.name = 'pistol_mag';
    grip.add(mag);
  }
  return mag;
}

/** Held / falling magazine clone (matches the seated M9 mag). */
export function makePistolMag(s: M9GripShape = defaultM9GripShape()): THREE.Group {
  const m = makeMats();
  const charcoal = new THREE.MeshStandardMaterial({
    color: 0x1a1d20,
    metalness: 0.58,
    roughness: 0.5,
  });
  const mag = new THREE.Group();
  mag.name = 'pistol_mag_held';
  fillPistolMagMeshes(mag, { charcoal, polymer: m.polymer, steelDark: m.steelDark }, s);
  mag.position.set(0, 0, 0);
  return mag;
}

function fillMp5MagMeshes(mag: THREE.Group, m: GunMats): void {
  add(mag, taperBox(0.02, 0.022, 0.048, 0.03), m.steelDark, 0, -0.03, 0);
  add(mag, taperBox(0.022, 0.024, 0.04, 0.032), m.steelDark, 0, -0.068, 0);
  add(mag, taperBox(0.024, 0.026, 0.014, 0.036), m.steelDark, 0, -0.092, 0);
  add(mag, new THREE.BoxGeometry(0.023, 0.003, 0.028), m.steel, 0, -0.048, 0);
  add(mag, new THREE.BoxGeometry(0.024, 0.003, 0.03), m.steel, 0, -0.078, 0);
}

function fillAk47MagMeshes(mag: THREE.Group, m: GunMats): void {
  const segs = 8;
  for (let i = 0; i < segs; i++) {
    const t = i / (segs - 1);
    const y = -0.006 - i * 0.016;
    const z = 0.001 - t * t * 0.036;
    const rx = t * 0.58;
    const w = 0.021 + t * 0.003;
    const d = 0.03 + t * 0.005;
    add(mag, new THREE.BoxGeometry(w, 0.017, d), m.steelDark, 0, y, z, rx, 0, 0);
    if (i % 2 === 0) {
      add(mag, new THREE.BoxGeometry(w + 0.0015, 0.003, d + 0.001), m.steel, 0, y, z, rx, 0, 0);
    }
  }
  add(mag, new THREE.BoxGeometry(0.02, 0.01, 0.028), m.steel, 0, 0.006, 0.002);
  add(mag, new THREE.BoxGeometry(0.018, 0.007, 0.014), m.steel, 0, 0.012, 0.004);
  const t = 1;
  add(
    mag,
    new THREE.BoxGeometry(0.025, 0.008, 0.036),
    m.steel,
    0,
    -0.006 - (segs - 1) * 0.016 - 0.008,
    0.001 - t * t * 0.036,
    0.58,
    0,
    0,
  );
}

/** M870 pump / AK forend: ribbed wood cylinder. */
function addRibbedWoodForend(parent: THREE.Object3D, wood: THREE.Material, y: number, zCenter: number): void {
  cap(parent, wood, 0, y, zCenter, 0.098, 0.016, Math.PI / 2, 0, 0, 1.18, 0.82);
  for (let i = 0; i < 6; i++) {
    cap(parent, wood, 0, y, zCenter + 0.04 - i * 0.016, 0.002, 0.017, Math.PI / 2, 0, 0, 1.22, 0.28);
  }
}

/** Held / falling magazine clone (matches the seated MP5 mag). */
export function makeMp5Mag(): THREE.Group {
  const mag = new THREE.Group();
  mag.name = 'mp5_mag_held';
  fillMp5MagMeshes(mag, makeMats());
  return mag;
}

/** Held / falling magazine clone (matches the seated AK mag). */
export function makeAk47Mag(): THREE.Group {
  const mag = new THREE.Group();
  mag.name = 'ak47_mag_held';
  fillAk47MagMeshes(mag, makeMats());
  return mag;
}

function fillM9Grip(grip: THREE.Group, mats: M9GripMats, s: M9GripShape): void {
  for (const child of [...grip.children]) {
    if (child.name !== 'grip_vis') continue;
    grip.remove(child);
    const mesh = child as THREE.Mesh;
    mesh.geometry?.dispose();
  }
  grip.position.set(0, s.y, s.z);
  grip.rotation.x = s.pitch;
  const bodyY = -s.height * 0.52;
  const vis = (mesh: THREE.Mesh) => {
    mesh.name = 'grip_vis';
    return mesh;
  };
  const sideH = s.height * 0.87;
  vis(
    add(
      grip,
      taperBox(s.topW * 0.28, s.botW * 0.27, sideH, s.depth * 0.87),
      mats.polymer,
      s.botW * 0.49,
      bodyY + 0.002,
      0.001,
    ),
  );
  vis(
    add(
      grip,
      taperBox(s.topW * 0.28, s.botW * 0.27, sideH, s.depth * 0.87),
      mats.polymer,
      -s.botW * 0.49,
      bodyY + 0.002,
      0.001,
    ),
  );
  vis(
    add(
      grip,
      taperBox(s.topW * 0.8, s.botW * 0.53, s.height * 0.66, s.depth * 0.4),
      mats.steelDark,
      0,
      bodyY - 0.006,
      -0.004,
    ),
  );
  vis(
    add(
      grip,
      extrudeProfile(
        [
          [0.01, 0.01],
          [0.018, 0.004],
          [0.004, 0.02],
        ],
        0.008,
        0.002,
      ),
      mats.charcoal,
      0,
      -0.006,
      0.012,
    ),
  );
  fillPistolMagMeshes(ensurePistolMag(grip), mats, s);
  grip.userData.m9Shape = { ...s };
}

function m9GripGroup(weapon: LoadedViewWeapon): THREE.Group | undefined {
  const named = weapon.oriented.getObjectByName('m9_grip');
  if (named) return named as THREE.Group;
  return weapon.gripR?.parent as THREE.Group | undefined;
}

export function readM9GripShape(weapon: LoadedViewWeapon): M9GripShape {
  const stored = m9GripGroup(weapon)?.userData.m9Shape as M9GripShape | undefined;
  return stored ? { ...stored } : defaultM9GripShape();
}

export function applyM9GripShape(weapon: LoadedViewWeapon, shape: M9GripShape): void {
  const grip = m9GripGroup(weapon);
  if (!grip) return;
  const mats = grip.userData.m9Mats as M9GripMats | undefined;
  if (!mats) return;
  fillM9Grip(grip, mats, shape);
  markView(weapon.root);
}

function wrap(oriented: THREE.Group, extra: Omit<LoadedViewWeapon, 'root' | 'oriented'>): LoadedViewWeapon {
  const root = new THREE.Group();
  oriented.rotation.order = 'YXZ';
  root.add(oriented);
  markView(root);
  return { root, oriented, ...extra };
}

/** Kept for a future Uzi weapon — not registered. */
export function buildUzi(m: GunMats = makeMats()): LoadedViewWeapon {
  const g = new THREE.Group();
  const slide = new THREE.Group();
  slide.name = 'slide';
  g.add(slide);
  add(g, new THREE.BoxGeometry(0.028, 0.038, 0.11), m.polymer, 0, -0.012, -0.02);
  add(g, new THREE.BoxGeometry(0.026, 0.072, 0.032), m.polymer, 0, -0.048, 0.028);
  add(g, new THREE.BoxGeometry(0.024, 0.068, 0.008), m.polymerSoft, 0.014, -0.048, 0.028);
  add(g, new THREE.BoxGeometry(0.024, 0.068, 0.008), m.polymerSoft, -0.014, -0.048, 0.028);
  add(slide, new THREE.BoxGeometry(0.026, 0.022, 0.155), m.steel, 0, 0.018, -0.04);
  barrel(slide, m.steelDark, -0.05, -0.175, 0.0065, 12, 0.0055);
  return wrap(g, {
    muzzle: socket('muzzle', 0, 0.012, -0.182, g),
    ironSight: socket('ironSight', 0, 0.04, -0.04, g),
    gripR: pistolGripR(g, 0.018, -0.05, 0.03),
    gripL: pistolGripL(g, -0.014, -0.048, 0.028),
    ejectPort: socket('eject', 0.02, 0.02, -0.05, g),
    slide,
  });
}

function buildM9(m: GunMats): LoadedViewWeapon {
  const g = new THREE.Group();
  const slide = new THREE.Group();
  slide.name = 'slide';
  g.add(slide);

  const charcoal = new THREE.MeshStandardMaterial({
    color: 0x1a1d20,
    metalness: 0.58,
    roughness: 0.5,
  });
  const slideMat = new THREE.MeshStandardMaterial({
    color: 0x15171a,
    metalness: 0.72,
    roughness: 0.4,
  });

  const rail = extrudeProfile(
    [
      [0.0, -0.154],
      [0.008, -0.156],
      [0.017, -0.148],
      [0.017, 0.038],
      [0.009, 0.048],
      [0.0, 0.046],
    ],
    0.0044,
    0.0016,
  );
  add(slide, rail, slideMat, 0.0106, 0.006, 0);
  add(slide, rail, slideMat, -0.0106, 0.006, 0);

  add(
    slide,
    extrudeProfile(
      [
        [0.002, -0.176],
        [0.016, -0.176],
        [0.023, -0.168],
        [0.023, -0.148],
        [0.01, -0.142],
        [0.002, -0.142],
      ],
      0.013,
      0.003,
    ),
    slideMat,
    0,
    0.006,
    0,
  );

  add(
    slide,
    extrudeProfile(
      [
        [0.002, 0.006],
        [0.018, 0.008],
        [0.026, 0.018],
        [0.026, 0.044],
        [0.014, 0.054],
        [0.002, 0.05],
      ],
      0.0132,
      0.0032,
    ),
    slideMat,
    0,
    0.006,
    0,
  );

  const groove = extrudeProfile(
    [
      [0.005, -0.0034],
      [0.016, -0.0012],
      [0.016, 0.0012],
      [0.005, 0.0034],
    ],
    0.0011,
    0,
  );
  for (let i = 0; i < 3; i++) {
    const z = 0.026 + i * 0.007;
    add(slide, groove, m.steelDark, 0.0136, 0.008, z);
    add(slide, groove, m.steelDark, -0.0136, 0.008, z);
  }

  add(
    slide,
    extrudeProfile(
      [
        [0.006, 0.046],
        [0.02, 0.048],
        [0.014, 0.066],
        [0.002, 0.058],
      ],
      0.0034,
      0.0008,
    ),
    charcoal,
    0,
    0.006,
    0,
  );

  add(slide, taperBox(0.0022, 0.0048, 0.009, 0.011), charcoal, 0, 0.03, -0.16);
  add(slide, new THREE.BoxGeometry(0.0022, 0.0026, 0.003), m.sight, 0, 0.0355, -0.16);

  add(slide, taperBox(0.015, 0.016, 0.0055, 0.011), charcoal, 0, 0.033, 0.036);
  add(slide, new THREE.BoxGeometry(0.0026, 0.0024, 0.0032), m.sight, 0.0048, 0.0366, 0.036);
  add(slide, new THREE.BoxGeometry(0.0026, 0.0024, 0.0032), m.sight, -0.0048, 0.0366, 0.036);

  add(slide, taperBox(0.002, 0.0042, 0.005, 0.012), charcoal, -0.0132, 0.02, 0.038, 0.15, 0, 0.35);

  const barrelMesh = barrel(g, m.steel, -0.036, -0.174, 0.0054, 8, 0.0048);
  barrelMesh.position.y = 0.0115;
  const chamber = barrel(g, charcoal, -0.03, -0.058, 0.0064, 8);
  chamber.position.y = 0.0115;
  add(g, new THREE.CylinderGeometry(0.0066, 0.005, 0.007, 8), charcoal, 0, 0.0115, -0.177, Math.PI / 2, 0, 0);

  add(
    g,
    extrudeProfile(
      [
        [0.005, -0.092],
        [0.007, -0.03],
        [0.007, 0.044],
        [-0.004, 0.04],
        [-0.014, 0.012],
        [-0.012, -0.04],
        [-0.006, -0.092],
      ],
      0.0115,
      0.0024,
    ),
    charcoal,
    0,
    0,
    0,
  );

  add(
    g,
    extrudeProfile(
      [
        [-0.012, 0.016],
        [-0.046, 0.012],
        [-0.052, -0.008],
        [-0.04, -0.03],
        [-0.014, -0.036],
        [-0.008, -0.024],
        [-0.03, -0.02],
        [-0.038, -0.004],
        [-0.032, 0.01],
        [-0.012, 0.008],
      ],
      0.0064,
      0.001,
    ),
    charcoal,
    0,
    0,
    0,
  );

  add(
    g,
    extrudeProfile(
      [
        [-0.016, 0.004],
        [-0.03, -0.002],
        [-0.018, 0.012],
      ],
      0.0028,
      0,
    ),
    m.steelDark,
    0,
    0,
    0,
  );

  const grip = new THREE.Group();
  grip.name = 'm9_grip';
  g.add(grip);
  const mats: M9GripMats = { charcoal, polymer: m.polymer, steelDark: m.steelDark };
  grip.userData.m9Mats = mats;
  fillM9Grip(grip, mats, defaultM9GripShape());

  return wrap(g, {
    muzzle: socket('muzzle', 0, 0.0115, -0.184, g),
    ironSight: socket('ironSight', 0, 0.037, 0.036, slide),
    gripR: pistolGripR(grip, 0.015, -0.016, 0.002),
    gripL: pistolGripL(g, -0.015, -0.022, 0.038),
    ejectPort: socket('eject', 0.014, 0.016, -0.048, slide),
    slide,
    mag: grip.getObjectByName('pistol_mag') ?? undefined,
    silencer: addPistolSilencer(g),
  });
}

function addPistolSilencer(g: THREE.Group): THREE.Group {
  const s = new THREE.Group();
  s.name = 'silencer';
  const can = new THREE.MeshStandardMaterial({
    color: 0x1c1e20,
    metalness: 0.5,
    roughness: 0.46,
  });
  const lip = new THREE.MeshStandardMaterial({
    color: 0x2c3034,
    metalness: 0.64,
    roughness: 0.34,
  });
  barrel(s, lip, 0.0, -0.01, 0.0068, 10, 0.0074);
  barrel(s, can, -0.01, -0.064, 0.0084, 12, 0.008);
  barrel(s, lip, -0.028, -0.032, 0.0088, 10);
  barrel(s, lip, -0.046, -0.05, 0.0088, 10);
  barrel(s, lip, -0.064, -0.072, 0.0076, 10, 0.0064);
  socket('silencer_tip', 0, 0, -0.074, s);
  s.position.set(0, 0.0115, -0.184);
  g.add(s);
  return s;
}

function buildM870(m: GunMats): LoadedViewWeapon {
  const g = new THREE.Group();

  add(g, topGrooveBox(0.03, 0.028, 0.15, 0.016, 0.011), m.steel, 0, 0.002, -0.02);
  add(g, new THREE.BoxGeometry(0.015, 0.0014, 0.148), m.steelDark, 0, 0.0055, -0.02);
  add(g, new THREE.BoxGeometry(0.016, 0.014, 0.05), m.steelDark, 0.016, 0.008, -0.03);
  add(g, new THREE.BoxGeometry(0.018, 0.008, 0.042), m.steelDark, 0, -0.012, -0.018);

  cap(g, m.walnut, 0, -0.04, 0.032, 0.05, 0.0125, 0.22, 0, 0, 0.86, 1.06);
  add(g, taperBox(0.02, 0.024, 0.012, 0.022), m.rubber, 0, -0.072, 0.038, 0.22, 0, 0);
  add(g, new THREE.BoxGeometry(0.005, 0.016, 0.01), m.steelDark, 0, -0.018, 0.016, 0.28, 0, 0);
  add(
    g,
    extrudeProfile(
      [
        [-0.012, 0.02],
        [-0.03, 0.012],
        [-0.034, -0.006],
        [-0.026, -0.018],
        [-0.01, -0.016],
        [-0.006, 0.0],
      ],
      0.0055,
      0.001,
    ),
    m.steelDark,
    0,
    0,
    0.008,
  );

  barrel(g, m.steel, -0.08, -0.48, 0.011, 16, 0.01);
  add(g, new THREE.BoxGeometry(0.007, 0.002, 0.38), m.steelDark, 0, 0.0102, -0.28);
  add(g, new THREE.BoxGeometry(0.006, 0.012, 0.01), m.steel, 0, 0.022, -0.46);
  const magTube = barrel(g, m.steelDark, -0.08, -0.38, 0.0078, 12);
  magTube.position.y = -0.017;
  add(g, new THREE.CylinderGeometry(0.01, 0.01, 0.012, 12), m.steel, 0, -0.017, -0.388, Math.PI / 2, 0, 0);

  const pump = new THREE.Group();
  pump.name = 'pump';
  g.add(pump);
  addRibbedWoodForend(pump, m.wood, -0.017, -0.20);

  addOvalStock(g, m.wood, m.walnut, -0.008, 0.05);

  const gripL = forendGripL(pump, 0.0, -0.03, -0.19);

  return wrap(g, {
    muzzle: socket('muzzle', 0, 0.0, -0.49, g),
    ironSight: socket('ironSight', 0, 0.028, -0.46, g),
    gripR: pistolGripR(g, 0.016, -0.04, 0.03),
    gripL,
    ejectPort: socket('eject', 0.02, 0.01, -0.03, g),
    pump,
  });
}

function addOvalStock(
  parent: THREE.Object3D,
  wood: THREE.Material,
  plate: THREE.Material,
  y0: number,
  z0: number,
): void {
  const stockRx = Math.PI / 2 + 0.12;
  const stockAy = Math.cos(stockRx);
  const stockAz = Math.sin(stockRx);
  const along = (t: number) => ({ y: y0 + stockAy * t, z: z0 + stockAz * t });
  const stockWide = 0.92;
  const stockTall = 2.45;
  const oval = (mesh: THREE.Mesh) => {
    mesh.scale.set(stockWide, 1, stockTall);
    return mesh;
  };
  const stockLen = 0.152;
  const stockR0 = 0.0095;
  const stockR1 = 0.0155;
  const stockMid = along(stockLen * 0.5);
  oval(
    add(
      parent,
      new THREE.CylinderGeometry(stockR1, stockR0, stockLen, 20),
      wood,
      0,
      stockMid.y,
      stockMid.z,
      stockRx,
      0,
      0,
    ),
  );
  const stockWrist = along(0);
  cap(parent, wood, 0, stockWrist.y, stockWrist.z, 0.01, stockR0, stockRx, 0, 0, stockWide, stockTall);
  const stockPad = 0.007;
  const stockButt = along(stockLen + stockPad * 0.5);
  oval(
    add(
      parent,
      new THREE.CylinderGeometry(stockR1 + 0.0012, stockR1 + 0.0012, stockPad, 20),
      plate,
      0,
      stockButt.y,
      stockButt.z,
      stockRx,
      0,
      0,
    ),
  );
}

function addShell(parent: THREE.Object3D, mats: GunMats, x: number, y: number, z: number): void {
  add(parent, new THREE.CylinderGeometry(0.0075, 0.0075, 0.0026, 16), mats.brass, x, y, z, Math.PI / 2, 0, 0);
  add(parent, new THREE.CylinderGeometry(0.0066, 0.0066, 0.018, 12), mats.brass, x, y, z - 0.009, Math.PI / 2, 0, 0);
  add(parent, new THREE.CylinderGeometry(0.0026, 0.0026, 0.0014, 10), mats.steelDark, x, y, z + 0.0016, Math.PI / 2, 0, 0);
}

/** Side-by-side chamber spacing (matches `buildSawedOff`). */
export const DOUBLE_BARREL_SHELL_SEP = 0.0162;

/** Two fresh shells for the break-reload insert, spaced like the chambers. */
export function makeDoubleReloadPair(): THREE.Group {
  const mats = makeMats();
  const pair = new THREE.Group();
  pair.name = 'reload_pair';
  addShell(pair, mats, -DOUBLE_BARREL_SHELL_SEP, 0, 0);
  addShell(pair, mats, DOUBLE_BARREL_SHELL_SEP, 0, 0);
  pair.userData.sep = DOUBLE_BARREL_SHELL_SEP;
  return pair;
}

/** Spent hull for the M870 side eject. */
export function makePumpCasing(): THREE.Group {
  const mats = makeMats();
  const shell = new THREE.Group();
  shell.name = 'pump_casing';
  addShell(shell, mats, 0, 0, 0);
  return shell;
}

/** Live shotgun shell for world pickups. */
export function makeLiveShell(): THREE.Group {
  const mats = makeMats();
  const shell = new THREE.Group();
  shell.name = 'live_shell';
  addShell(shell, mats, 0, 0, 0);
  return shell;
}

function buildSawedOff(m: GunMats): LoadedViewWeapon {
  const g = new THREE.Group();
  const sep = DOUBLE_BARREL_SHELL_SEP;
  const sepFront = 0.011;
  const br = 0.0158;
  const brFront = 0.0112;
  const bore = 0.0104;
  const boreFront = 0.0078;

  const frame = new THREE.Group();
  frame.name = 'frame';
  g.add(frame);
  cap(frame, m.steel, 0, 0.002, 0.072, 0.01, 0.017, Math.PI / 2, 0, 0, 1.92, 1.12);
  cap(frame, m.steel, 0.02, 0.0, 0.058, 0.032, 0.013, Math.PI / 2, 0, 0, 0.62, 1.18);
  cap(frame, m.steel, -0.02, 0.0, 0.058, 0.032, 0.013, Math.PI / 2, 0, 0, 0.62, 1.18);
  cap(frame, m.steel, 0, 0.014, 0.066, 0.024, 0.009, Math.PI / 2, 0, 0, 1.55, 0.72);
  cap(frame, m.steel, 0, -0.014, 0.048, 0.03, 0.01, Math.PI / 2, 0, 0, 1.75, 0.68);
  const lever = add(frame, new THREE.BoxGeometry(0.0076, 0.004, 0.012), m.steel, 0, 0.018, 0.072, 0.22, 0, 0);
  const leverMid = add(lever, new THREE.BoxGeometry(0.0072, 0.004, 0.012), m.steel, 0, 0.0028, 0.0075, 0.3, 0, 0);
  add(leverMid, new THREE.BoxGeometry(0.0068, 0.0038, 0.011), m.steel, 0, 0.0026, 0.007, 0.28, 0, 0);
  add(frame, new THREE.SphereGeometry(0.0034, 8, 8), m.steelDark, -sep, 0.0, 0.066);
  add(frame, new THREE.SphereGeometry(0.0034, 8, 8), m.steelDark, sep, 0.0, 0.066);
  add(frame, new THREE.CylinderGeometry(0.0034, 0.0034, 0.052, 8), m.steelDark, 0, -0.014, 0.04, 0, 0, Math.PI / 2);

  cap(frame, m.walnut, 0, -0.042, 0.058, 0.048, 0.013, 0.28, 0, 0, 0.86, 1.08);
  add(frame, taperBox(0.022, 0.026, 0.012, 0.024), m.rubber, 0, -0.076, 0.066, 0.28, 0, 0);
  add(frame, new THREE.BoxGeometry(0.005, 0.016, 0.01), m.steelDark, 0, -0.018, 0.038, 0.28, 0, 0);
  add(
    frame,
    extrudeProfile(
      [
        [-0.012, 0.02],
        [-0.028, 0.012],
        [-0.032, -0.006],
        [-0.024, -0.018],
        [-0.01, -0.016],
        [-0.006, 0.0],
      ],
      0.005,
      0.001,
    ),
    m.steelDark,
    0,
    0,
    0.042,
  );
  addOvalStock(frame, m.wood, m.walnut, -0.008, 0.078);

  const hingeY = -0.014;
  const hingeZ = 0.04;
  const barrelBreak = new THREE.Group();
  barrelBreak.name = 'break';
  barrelBreak.position.set(0, hingeY, hingeZ);
  g.add(barrelBreak);
  const barrels = new THREE.Group();
  barrels.position.set(0, -hingeY, -hingeZ);
  barrelBreak.add(barrels);
  const barrelLen = 0.42;
  const barrelZ = -0.16;
  const barrelYaw = Math.atan2(sep - sepFront, barrelLen);
  const barrelX = (sep + sepFront) * 0.5;
  const muzzleZ = barrelZ - barrelLen * 0.5;
  const barrelShine = new THREE.MeshStandardMaterial({
    color: 0x8a96a2,
    metalness: 0.97,
    roughness: 0.12,
  });
  add(
    barrels,
    taperHollow(br, brFront, bore, boreFront, barrelLen, 16),
    barrelShine,
    -barrelX,
    0,
    barrelZ,
    Math.PI / 2,
    -barrelYaw,
    0,
  );
  add(
    barrels,
    taperHollow(br, brFront, bore, boreFront, barrelLen, 16),
    barrelShine,
    barrelX,
    0,
    barrelZ,
    Math.PI / 2,
    barrelYaw,
    0,
  );
  add(barrels, hollowTube(br + 0.0015, 0.0078, 0.026, 18), barrelShine, -sep, 0, 0.046);
  add(barrels, hollowTube(br + 0.0015, 0.0078, 0.026, 18), barrelShine, sep, 0, 0.046);
  cap(barrels, barrelShine, 0, -0.006, 0.046, 0.018, 0.01, Math.PI / 2, 0, 0, 1.55, 0.78);
  cap(barrels, barrelShine, 0, brFront + 0.001, -0.14, 0.34, 0.0022, Math.PI / 2, 0, 0, 0.85, 0.7);
  cap(barrels, m.alloy, 0, 0.003, 0.058, 0.006, 0.0026, Math.PI / 2, 0, 0, 2.4, 0.7);
  cap(barrels, m.alloy, 0, -0.004, 0.057, 0.008, 0.0028, 0.15, 0, 0, 0.7, 1.05);
  const shellL = new THREE.Group();
  shellL.name = 'shell_l';
  shellL.position.set(-sep, 0, 0.058);
  barrels.add(shellL);
  const shellR = new THREE.Group();
  shellR.name = 'shell_r';
  shellR.position.set(sep, 0, 0.058);
  barrels.add(shellR);
  addShell(shellL, m, 0, 0, 0);
  addShell(shellR, m, 0, 0, 0);
  cap(barrels, m.wood, 0, -0.013, -0.06, 0.1, 0.013, Math.PI / 2, 0, 0, 2.2, 0.72);

  return wrap(g, {
    muzzle: socket('muzzle', 0, 0.0, muzzleZ, g),
    ironSight: socket('ironSight', 0, brFront + 0.006, muzzleZ + 0.01, g),
    gripR: pistolGripR(frame, 0.016, -0.04, 0.055),
    gripL: forendGripL(barrels, 0.0, -0.03, -0.06),
    ejectPort: socket('eject', 0.0, 0.01, 0.058, barrels),
    barrelBreak,
    shellL,
    shellR,
  });
}

function buildMp5(m: GunMats): LoadedViewWeapon {
  const g = new THREE.Group();
  const glass = opticGlassMat();

  add(g, taperBox(0.03, 0.034, 0.038, 0.16), m.polymer, 0, 0.002, -0.04);
  cap(g, m.steel, 0, 0.022, -0.048, 0.1, 0.008, Math.PI / 2, 0, 0, 1.7, 0.52);
  add(g, hollowTube(0.01, 0.0074, 0.14, 12), m.steel, 0, 0.038, -0.05);

  add(g, squareFrame(0.026, 0.024, 0.02, 0.018, 0.012), m.steelDark, 0, 0.038, 0.058);
  const plate = add(g, new THREE.PlaneGeometry(0.0185, 0.0165), glass, 0, 0.038, 0.055);
  plate.renderOrder = 2;
  add(g, squareFrame(0.022, 0.02, 0.016, 0.0145, 0.042), m.steel, 0, 0.038, 0.039);
  add(g, squareFrame(0.02, 0.018, 0.014, 0.013, 0.014), m.steel, 0, 0.038, 0.02);
  add(g, new THREE.BoxGeometry(0.016, 0.01, 0.044), m.steelDark, 0, 0.025, 0.039);

  cap(g, m.polymer, 0, -0.038, 0.03, 0.05, 0.012, 0.2, 0, 0, 0.88, 1.04);
  add(g, taperBox(0.02, 0.024, 0.012, 0.022), m.rubber, 0, -0.07, 0.036, 0.2, 0, 0);
  add(g, new THREE.BoxGeometry(0.005, 0.015, 0.01), m.steel, 0, -0.018, 0.012, 0.28, 0, 0);
  add(
    g,
    extrudeProfile(
      [
        [-0.012, 0.016],
        [-0.028, 0.01],
        [-0.032, -0.008],
        [-0.026, -0.018],
        [-0.01, -0.016],
        [-0.006, -0.002],
      ],
      0.0055,
      0.001,
    ),
    m.steelDark,
    0,
    0,
    0,
  );

  add(g, new THREE.BoxGeometry(0.024, 0.024, 0.034), m.polymer, 0, -0.022, -0.04);

  const mag = new THREE.Group();
  mag.name = 'mp5_mag';
  mag.position.set(0, -0.018, -0.04);
  mag.rotation.x = 0.08;
  g.add(mag);
  fillMp5MagMeshes(mag, m);

  cap(g, m.polymerSoft, 0, -0.006, -0.14, 0.078, 0.013, Math.PI / 2, 0, 0, 1.28, 0.76);
  for (let i = 0; i < 4; i++) {
    cap(g, m.polymer, 0, 0.008, -0.108 - i * 0.02, 0.002, 0.012, Math.PI / 2, 0, 0, 1.32, 0.22);
  }

  barrel(g, m.steelDark, -0.18, -0.34, 0.007, 12, 0.006);
  cap(g, m.steel, 0, 0.002, -0.22, 0.018, 0.011, Math.PI / 2, 0, 0, 1.05, 1);
  add(g, new THREE.BoxGeometry(0.01, 0.024, 0.014), m.steelDark, 0, 0.012, -0.22);
  add(g, taperBox(0.012, 0.016, 0.018, 0.016), m.steelDark, 0, 0.026, -0.22);
  add(g, new THREE.BoxGeometry(0.0028, 0.01, 0.003), m.sight, 0, 0.038, -0.22);

  add(g, taperBox(0.03, 0.032, 0.03, 0.03), m.polymer, 0, 0.0, 0.048);
  cap(g, m.steel, 0.012, 0.008, 0.095, 0.11, 0.0028, Math.PI / 2, 0, 0, 1, 1);
  cap(g, m.steel, -0.012, 0.008, 0.095, 0.11, 0.0028, Math.PI / 2, 0, 0, 1, 1);
  add(g, new THREE.BoxGeometry(0.028, 0.008, 0.014), m.polymer, 0, 0.008, 0.048);
  cap(g, m.polymer, 0, 0.0, 0.118, 0.04, 0.01, Math.PI / 2, 0, 0, 1.05, 0.72);
  add(g, new THREE.BoxGeometry(0.028, 0.008, 0.012), m.polymer, 0, 0.008, 0.118);
  add(g, taperBox(0.032, 0.034, 0.04, 0.014), m.rubber, 0, -0.004, 0.158);
  add(g, new THREE.BoxGeometry(0.03, 0.012, 0.01), m.steelDark, 0, 0.008, 0.15);

  return wrap(g, {
    muzzle: socket('muzzle', 0, 0.002, -0.345, g),
    ironSight: socket('ironSight', 0, 0.038, 0.055, g),
    gripR: pistolGripR(g, 0.016, -0.038, 0.032),
    gripL: forendGripL(g, 0.0, -0.014, -0.14),
    ejectPort: socket('eject', 0.02, 0.016, -0.06, g),
    mag,
  });
}

function buildAk47(m: GunMats): LoadedViewWeapon {
  const g = new THREE.Group();

  add(
    g,
    extrudeProfile(
      [
        [0.022, -0.122],
        [0.024, -0.08],
        [0.022, 0.036],
        [0.028, 0.052],
        [0.02, 0.078],
        [0.006, 0.09],
        [-0.014, 0.09],
        [-0.016, 0.048],
        [-0.01, 0.018],
        [-0.018, -0.008],
        [-0.016, -0.058],
        [0.0, -0.112],
        [0.01, -0.126],
      ],
      0.016,
      0.0024,
    ),
    m.steel,
    0,
    0,
    0,
  );
  add(g, taperBox(0.028, 0.03, 0.014, 0.15), m.steelDark, 0, 0.026, -0.034);
  add(g, new THREE.BoxGeometry(0.03, 0.01, 0.14), m.steel, 0, 0.033, -0.034);
  for (const z of [-0.09, -0.05, -0.01, 0.03]) {
    cap(g, m.steelDark, 0.0165, 0.006, z, 0.0006, 0.0024, 0, 0, Math.PI / 2, 1, 0.8);
    cap(g, m.steelDark, -0.0165, 0.006, z, 0.0006, 0.0024, 0, 0, Math.PI / 2, 1, 0.8);
  }

  add(g, new THREE.BoxGeometry(0.0035, 0.024, 0.007), m.steelDark, 0.018, 0.002, 0.03, 0.12, 0, 0.22);
  cap(g, m.steelDark, 0.019, -0.004, 0.042, 0.018, 0.0022, 0.2, 0, 0.4, 0.7, 1);

  cap(g, m.bakelite, 0, -0.038, 0.03, 0.05, 0.0125, 0.22, 0, 0, 0.86, 1.06);
  add(g, taperBox(0.02, 0.024, 0.012, 0.022), m.rubber, 0, -0.07, 0.038, 0.22, 0, 0);
  add(g, new THREE.BoxGeometry(0.0045, 0.015, 0.009), m.steelDark, 0, -0.016, 0.012, 0.3, 0, 0);
  add(
    g,
    extrudeProfile(
      [
        [-0.012, 0.016],
        [-0.03, 0.01],
        [-0.034, -0.008],
        [-0.028, -0.02],
        [-0.012, -0.018],
        [-0.008, -0.004],
      ],
      0.0055,
      0.001,
    ),
    m.steelDark,
    0,
    0,
    0,
  );

  add(g, new THREE.BoxGeometry(0.026, 0.014, 0.04), m.steelDark, 0, -0.018, -0.042);

  const mag = new THREE.Group();
  mag.name = 'ak47_mag';
  mag.position.set(0, -0.018, -0.042);
  g.add(mag);
  fillAk47MagMeshes(mag, m);

  // Barrel starts inside the receiver so the nose is one piece.
  add(g, new THREE.BoxGeometry(0.03, 0.026, 0.04), m.steel, 0, 0.002, -0.112);
  barrel(g, m.steelDark, -0.1, -0.46, 0.0072, 12, 0.0064);

  // Same ribbed wood forend as the M870 pump, clamped on the barrel.
  addRibbedWoodForend(g, m.wood, -0.017, -0.175);
  add(g, new THREE.BoxGeometry(0.022, 0.012, 0.078), m.wood, 0, 0.016, -0.168);

  // Gas tube + block sit on the barrel (no floating mid-barrel slab).
  add(g, new THREE.CylinderGeometry(0.0036, 0.0036, 0.2, 8), m.steel, 0, 0.015, -0.21, Math.PI / 2, 0, 0);
  add(g, new THREE.BoxGeometry(0.016, 0.02, 0.024), m.steel, 0, 0.003, -0.31);
  add(g, new THREE.BoxGeometry(0.012, 0.01, 0.018), m.steelDark, 0, 0.016, -0.31);

  // Front sight block wraps the muzzle; post sits on that block.
  add(g, new THREE.BoxGeometry(0.016, 0.02, 0.028), m.steel, 0, 0.003, -0.448);
  add(g, new THREE.BoxGeometry(0.012, 0.014, 0.018), m.steelDark, 0, 0.016, -0.448);
  add(g, new THREE.BoxGeometry(0.0028, 0.012, 0.0032), m.sight, 0, 0.026, -0.448);
  add(g, new THREE.CylinderGeometry(0.008, 0.008, 0.014, 10), m.steel, 0, 0, -0.468, Math.PI / 2, 0, 0);

  add(g, taperBox(0.015, 0.017, 0.01, 0.022), m.steelDark, 0, 0.04, -0.055);
  add(g, new THREE.BoxGeometry(0.0036, 0.01, 0.011), m.sight, 0.005, 0.05, -0.055);
  add(g, new THREE.BoxGeometry(0.0036, 0.01, 0.011), m.sight, -0.005, 0.05, -0.055);

  addOvalStock(g, m.wood, m.walnut, -0.01, 0.082);

  return wrap(g, {
    muzzle: socket('muzzle', 0, 0.0, -0.475, g),
    ironSight: socket('ironSight', 0, 0.052, -0.055, g),
    gripR: pistolGripR(g, 0.016, -0.04, 0.03),
    gripL: forendGripL(g, 0.0, -0.028, -0.175),
    ejectPort: socket('eject', 0.02, 0.014, -0.05, g),
    mag,
  });
}

export function buildPunchFists(): LoadedViewWeapon {
  const g = new THREE.Group();
  const gripR = gripAt('grip_r', g, 0.16, -0.28, -0.3, [0, 0.2, 0.98], [0, -1, 0]);
  const gripL = gripAt('grip_l', g, -0.16, -0.3, -0.28, [0, 0.2, 0.98], [0, -1, 0]);
  const muzzle = socket('muzzle', 0, 0, -0.55, g);
  return wrap(g, {
    muzzle,
    ironSight: muzzle,
    gripR,
    gripL,
  });
}

export function buildGrenadeLauncher(m: GunMats = makeMats()): LoadedViewWeapon {
  const g = new THREE.Group();
  add(g, new THREE.CylinderGeometry(0.028, 0.03, 0.42, 14), m.steelDark, 0, 0.01, -0.12, Math.PI / 2, 0, 0);
  add(g, new THREE.CylinderGeometry(0.034, 0.034, 0.06, 14), m.alloy, 0, 0.01, 0.08, Math.PI / 2, 0, 0);
  add(g, new THREE.CylinderGeometry(0.046, 0.046, 0.07, 16), m.bakelite, 0, -0.012, 0.02, 0, 0, Math.PI / 2);
  add(g, new THREE.BoxGeometry(0.028, 0.07, 0.036), m.polymer, 0, -0.05, 0.06);
  add(g, new THREE.BoxGeometry(0.022, 0.018, 0.08), m.wood, 0, -0.02, -0.22);
  add(g, new THREE.BoxGeometry(0.01, 0.02, 0.03), m.sight, 0, 0.036, 0.02);
  const muzzle = socket('muzzle', 0, 0.01, -0.34, g);
  return wrap(g, {
    muzzle,
    ironSight: socket('sight', 0, 0.038, 0.02, g),
    gripR: pistolGripR(g, 0.016, -0.05, 0.06),
    gripL: forendGripL(g, 0.0, -0.02, -0.2),
  });
}

export function buildRpg(m: GunMats = makeMats()): LoadedViewWeapon {
  const g = new THREE.Group();
  const tube = new THREE.MeshStandardMaterial({ color: 0x2a3a24, metalness: 0.42, roughness: 0.62 });
  const tubeHi = new THREE.MeshStandardMaterial({ color: 0x354828, metalness: 0.38, roughness: 0.55 });
  const tubeRear = new THREE.MeshStandardMaterial({ color: 0x243220, metalness: 0.28, roughness: 0.68 });
  add(g, new THREE.CylinderGeometry(0.026, 0.026, 0.62, 14), tube, 0, 0.02, -0.08, Math.PI / 2, 0, 0);
  add(g, new THREE.CylinderGeometry(0.038, 0.022, 0.1, 12), tubeHi, 0, 0.02, -0.42, Math.PI / 2, 0, 0);
  add(g, new THREE.CylinderGeometry(0.032, 0.032, 0.08, 12), tubeRear, 0, 0.02, 0.22, Math.PI / 2, 0, 0);
  add(g, new THREE.BoxGeometry(0.03, 0.072, 0.04), m.polymer, 0, -0.04, 0.08);
  add(g, new THREE.BoxGeometry(0.08, 0.008, 0.05), tubeRear, 0, 0.055, 0.04);
  add(g, new THREE.BoxGeometry(0.012, 0.022, 0.04), m.sight, 0.03, 0.05, 0.02);
  const muzzle = socket('muzzle', 0, 0.02, -0.48, g);
  const rocket = createRocketMesh({ scale: 0.88, glow: false });
  rocket.name = 'rpg_rocket';
  rocket.position.set(0, 0.02, -0.52);
  g.add(rocket);
  return wrap(g, {
    muzzle,
    ironSight: socket('sight', 0.03, 0.055, 0.02, g),
    gripR: pistolGripR(g, 0.016, -0.04, 0.08),
    gripL: forendGripL(g, 0.0, 0.0, -0.12),
    rocket,
  });
}

export function buildProcPickup(id: WeaponId): THREE.Group {
  const mats = makeMats();
  switch (id) {
    case 'pistol':
      return buildM9(mats).root;
    case 'shotgun':
      return buildM870(mats).root;
    case 'sawedoff':
      return buildSawedOff(mats).root;
    case 'mp5':
      return buildMp5(mats).root;
    case 'ak47':
      return buildAk47(mats).root;
    case 'punch':
      return buildPunchFists().root;
    case 'gl':
      return buildGrenadeLauncher(mats).root;
    case 'rpg':
      return buildRpg(mats).root;
  }
}

export function buildSawedOffPickup(): THREE.Group {
  return buildProcPickup('sawedoff');
}

export function buildAllProcWeapons(): Record<WeaponId, LoadedViewWeapon> {
  const mats = makeMats();
  return {
    pistol: buildM9(mats),
    shotgun: buildM870(mats),
    sawedoff: buildSawedOff(mats),
    mp5: buildMp5(mats),
    ak47: buildAk47(mats),
    punch: buildPunchFists(),
    gl: buildGrenadeLauncher(mats),
    rpg: buildRpg(mats),
  };
}
